import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatConfigResponse } from '@/api/ApiClient';

interface AllConfigsTabProps {
  configs: ChatConfigResponse[];
  onAdd: () => void;
  onEdit: (config: ChatConfigResponse) => void;
  onTest: (config: ChatConfigResponse) => void;
  onSetDefault: (config: ChatConfigResponse) => void;
  onDelete: (configId: string) => void;
}

export default function AllConfigsTab({
  configs,
  onAdd,
  onEdit,
  onTest,
  onSetDefault,
  onDelete,
}: AllConfigsTabProps) {
  const { t } = useTranslation();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('chatConfig.tabs.allConfigurations')}</Typography>
        <Button 
          startIcon={<AddIcon />}
          variant="contained"
          onClick={onAdd}
        >
          {t('chatConfig.actions.addConfiguration')}
        </Button>
      </Box>
      
      <List>
        {configs.map((config, index) => (
          <React.Fragment key={config.id || index}>
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {config.display_name || `${config.provider_type} Configuration`}
                    {config.is_default && (
                      <Chip 
                        label={t('chatConfig.currentConfig.default')} 
                        color="primary" 
                        size="small" 
                        icon={<StarIcon />} 
                      />
                    )}
                    {config.is_active && (
                      <Chip 
                        label={t('chatConfig.currentConfig.active')} 
                        color="success" 
                        size="small" 
                      />
                    )}
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <span>
                      {t('chatConfig.currentConfig.provider')}: {config.provider_type} | {' '}
                      {t('chatConfig.currentConfig.model')}: {config.model} | {' '}
                      {t('chatConfig.currentConfig.temperature')}: {config.temperature} | {' '}
                      {t('chatConfig.currentConfig.maxTokens')}: {config.max_tokens}
                    </span>
                    {config.description && (
                      <span style={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {config.description}
                      </span>
                    )}
                  </React.Fragment>
                }
              />
              <ListItemSecondaryAction>
                <IconButton 
                  onClick={() => onTest(config)}
                  title={t('chatConfig.actions.testConfiguration')}
                >
                  <TestIcon />
                </IconButton>
                {!config.is_default && (
                  <IconButton 
                    onClick={() => onSetDefault(config)}
                    title={t('chatConfig.actions.setAsDefault')}
                    color="primary"
                  >
                    <StarBorderIcon />
                  </IconButton>
                )}
                {config.id !== "env-default" && (
                  <IconButton 
                    onClick={() => onEdit(config)}
                    title={t('chatConfig.actions.editConfiguration')}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                {config.id && config.id !== "env-default" && (
                  <IconButton 
                    onClick={() => onDelete(config.id!)}
                    title={t('chatConfig.actions.deleteConfiguration')}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
            {index < configs.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
}